import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    async function subscribe(e) {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/newsletter/subscribe", { email });
            toast.success("Subscribed! Thanks for joining our newsletter.");
            setEmail("");
        } catch (err) {
            toast.error("Could not subscribe. Try again.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <footer className="bg-ink text-white mt-24" data-testid="site-footer">
            {/* Newsletter CTA */}
            <div className="border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="overline text-primary mb-3">Stay in the loop</div>
                        <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                            Get product updates, news & promos straight to your inbox.
                        </h3>
                    </div>
                    <form onSubmit={subscribe} className="flex gap-0" data-testid="newsletter-form">
                        <input
                            type="email"
                            required
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 bg-white/5 border border-white/20 px-4 py-3 placeholder-white/40 focus:outline-none focus:border-primary"
                            data-testid="newsletter-email-input"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-primary text-white font-semibold px-6 py-3 hover:bg-primary-hover transition-colors disabled:opacity-50"
                            data-testid="newsletter-subscribe-btn"
                        >
                            {loading ? "..." : "Subscribe"}
                        </button>
                    </form>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 grid grid-cols-2 md:grid-cols-5 gap-8">
                <div className="col-span-2">
                    <div className="flex items-center gap-2.5 mb-4">
                        <img src="/logo.png" alt="Peppa Target Services" className="h-14 w-auto bg-white rounded p-1" />
                        <div>
                            <div className="font-display font-bold">Peppa Target</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Services Ltd</div>
                        </div>
                    </div>
                    <p className="text-sm text-white/70 leading-relaxed max-w-sm">
                        Premium home detergents, enterprise ICT services, and bespoke software – all delivered with uncompromising quality.
                    </p>
                    <div className="flex gap-3 mt-6">
                        {[Facebook, Twitter, Linkedin, Instagram].map((Icon, i) => (
                            <a key={i} href="#" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors">
                                <Icon className="w-4 h-4" />
                            </a>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="overline text-white/60 mb-4">Company</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/about" className="text-white/80 hover:text-white">About us</Link></li>
                        <li><Link to="/services" className="text-white/80 hover:text-white">Services</Link></li>
                        <li><Link to="/news" className="text-white/80 hover:text-white">News</Link></li>
                        <li><Link to="/careers" className="text-white/80 hover:text-white">Careers</Link></li>
                        <li><Link to="/gallery" className="text-white/80 hover:text-white">Gallery</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="overline text-white/60 mb-4">Shop</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/products" className="text-white/80 hover:text-white">All products</Link></li>
                        <li><Link to="/products?category=detergents" className="text-white/80 hover:text-white">Detergents</Link></li>
                        <li><Link to="/products?category=ict" className="text-white/80 hover:text-white">ICT services</Link></li>
                        <li><Link to="/products?category=software" className="text-white/80 hover:text-white">Software</Link></li>
                        <li><Link to="/downloads" className="text-white/80 hover:text-white">Downloads</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="overline text-white/60 mb-4">Contact</div>
                    <ul className="space-y-3 text-sm">
                        <li className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 mt-0.5 text-primary" />
                            <span className="text-white/80">Plot 45, Industrial Area<br/>Kampala, Uganda</span>
                        </li>
                        <li className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-primary" />
                            <a href="tel:+256700000000" className="text-white/80 hover:text-white">+256 700 000 000</a>
                        </li>
                        <li className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-primary" />
                            <a href="mailto:hello@peppatarget.com" className="text-white/80 hover:text-white">hello@peppatarget.com</a>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
                    <div>© {new Date().getFullYear()} Peppa Target Services Limited. All rights reserved.</div>
                    <div className="flex gap-4">
                        <Link to="/contact" className="hover:text-white">Support</Link>
                        <a href="#" className="hover:text-white">Privacy</a>
                        <a href="#" className="hover:text-white">Terms</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
